/**
 * 支付模态框组件
 *
 * 负责处理 PayPal 和 Facebook 支付的完整流程：
 * 1. 显示订单摘要（商品信息、价格计算）
 * 2. 集成 PayPal 支付按钮
 * 3. 处理支付创建、验证、成功/失败回调
 * 4. 显示支付状态反馈
 */

import { FUNDING, PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useEffect, useState } from 'react';
import { API_ENDPOINTS, fetchWithRetry, getApiUrl, PRODUCT_TYPE } from '../config/api';
import useStore from '../store/useStore';
import { getProductIcon, getProductTypeName } from '../utils/productPresentation';
import { getBackendRewardAmount } from '../utils/productUtils';

// PayPal client id 跟随 Vite mode 读取，dev/sandbox 使用沙盒，prod 使用正式环境。
const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID;

// 全局订单 ID（用于支付验证）
let order_id;

const toSafeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

// 最终展示数量：直接取后端 Rewards 里主币种的值，不走前端兜底
const getServerRewardValue = (product) => toSafeNumber(getBackendRewardAmount(product));

/**
 * PayPal Logo 组件
 * @param {string} className - 额外的 CSS 类名
 */
function PayPalWordmark({ className = "" }) {
  return (
    <span
      aria-label="PayPal"
      className={`inline-flex items-baseline gap-[0.01em] italic font-black tracking-[-0.05em] ${className}`}
    >
      <span className="text-[#003087]">Pay</span>
      <span className="text-[#009cde]">Pal</span>
    </span>
  );
}

/**
 * 自定义 PayPal 按钮组件
 * 自己画按钮，点击后调用 PayPal SDK 弹出支付窗口，完全避免 iframe 白底问题
 */
/**
 * 自定义 PayPal 按钮组件
 * 自己画按钮，点击后调用 PayPal SDK 弹出支付窗口，完全避免 iframe 白底问题
 */
function CustomPayPalButton({ createOrder, onApprove, onError, onCancel }) {
  const createOrderAdapter = async (data, actions) => {
    const orderID = await createOrder?.(data, actions);
    if (!orderID) {
      throw new Error('PayPal order id is empty');
    }
    return orderID;
  };

  const onApproveAdapter = async (data, actions) => {
    await onApprove?.(data, actions);
  };

  return (
    <div className="w-full min-w-0 space-y-2.5">
      <div className="paypal-btn-shell w-full min-w-0">
        <PayPalButtons
          fundingSource={FUNDING.PAYPAL}
          style={{
            shape: 'rect',
            borderRadius: 16,
            label: 'paypal',
            color: 'blue',
            height: 46,
            tagline: false,
            disableMaxWidth: true
          }}
          createOrder={createOrderAdapter}
          onApprove={onApproveAdapter}
          onError={onError}
          onCancel={onCancel}
        />
      </div>
      <div className="paypal-btn-shell w-full min-w-0">
        <PayPalButtons
          fundingSource={FUNDING.CARD}
          style={{
            shape: 'rect',
            borderRadius: 16,
            label: 'pay',
            color: 'white',
            height: 46,
            tagline: false,
            disableMaxWidth: true
          }}
          createOrder={createOrderAdapter}
          onApprove={onApproveAdapter}
          onShippingChange={(data, actions) => {
            if (actions?.resolve) return actions.resolve();
            return true;
          }}
          onError={onError}
          onCancel={onCancel}
        />
      </div>
    </div>
  );
}

/**
 * 支付模态框组件
 *
 * @param {Object} props
 * @param {boolean} props.open - 是否显示模态框
 * @param {Function} props.onClose - 关闭回调
 * @param {Object} props.product - 选中的商品信息
 *   - id: 商品ID
 *   - baseValue: 基础数量
 *   - finalValue: 最终数量（含活动加成）
 *   - extraRate: 活动加成百分比
 *   - price: 原价
 *   - paypalPrice: PayPal支付价格（可能有折扣）
 *   - currency: 货币类型
 *   - productType: 商品类型（1100=金币, 1200=钻石）
 */
const PaymentModal = ({ open, onClose, product }) => {
  // -----------------------------------------
  // 本地状态
  // -----------------------------------------

  /** 支付方式：0=PayPal, 1=Facebook Pay */
  const [paymentMethod, setPaymentMethod] = useState(0);

  /** 支付状态：null | { type: 'success'|'error'|'info', message: string } */
  const [paymentStatus, setPaymentStatus] = useState(null);

  /** 支付失败弹窗是否显示 */
  const [paymentFailedOpen, setPaymentFailedOpen] = useState(false);

  /** 失败提示信息 */
  const [failedMessage, setFailedMessage] = useState('');

  // -----------------------------------------
  // 全局状态
  // -----------------------------------------

  const user = useStore((state) => state.user);
  const coinSaleActivity = useStore((state) => state.coinSaleActivity);
  const gemSaleActivity = useStore((state) => state.gemSaleActivity);
  const { setApiLoading } = useStore();
  const gameToken = user?.gameToken;

  const getTypeCIDsForProduct = () => {
    const productType = Number(product?.productType);
    const isCoinProduct = productType === PRODUCT_TYPE.NEW_STORE_COIN || productType === PRODUCT_TYPE.BASE_COIN;
    const isGemProduct = productType === PRODUCT_TYPE.NEW_STORE_GEM || productType === PRODUCT_TYPE.BASE_GEM;
    const activity = isCoinProduct ? coinSaleActivity : isGemProduct ? gemSaleActivity : null;
    console.log('[payment-debug][TypeCIDs][activity-raw]', {
      productType,
      isCoinProduct,
      isGemProduct,
      activity,
    });
    const activityId = activity?.ID;
    // 后端要求 TypeCIDs 传 number[]：
    // 1) 先把活动 ID 显式转为 number；
    // 2) 只有在 number 有效时才放入数组；
    // 3) 无有效活动 ID 时返回空数组，避免传错类型。
    const activityIdNumber = Number(activityId);
    return Number.isFinite(activityIdNumber) ? [activityIdNumber] : [];
  };

  // 是否显示 Facebook 支付（当前已禁用）
  const showFacebookPay = false;

  // -----------------------------------------
  // 副作用
  // -----------------------------------------

  /**
   * 弹窗打开时锁定页面滚动（尤其是移动端）：
   * - 防止背景商品列表跟随滚动
   * - 关闭弹窗后恢复原始滚动位置
   */
  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const { body, documentElement } = document;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollbarCompensation = window.innerWidth - documentElement.clientWidth;

    const prevStyles = {
      htmlOverflow: documentElement.style.overflow,
      htmlOverscrollBehavior: documentElement.style.overscrollBehavior,
      htmlScrollBehavior: documentElement.style.scrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
      bodyScrollBehavior: body.style.scrollBehavior,
      bodyPaddingRight: body.style.paddingRight,
    };

    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overscrollBehavior = 'none';
    if (scrollbarCompensation > 0) {
      body.style.paddingRight = `${scrollbarCompensation}px`;
    }

    return () => {
      documentElement.style.overflow = prevStyles.htmlOverflow;
      documentElement.style.overscrollBehavior = prevStyles.htmlOverscrollBehavior;

      body.style.overflow = prevStyles.bodyOverflow;
      body.style.position = prevStyles.bodyPosition;
      body.style.top = prevStyles.bodyTop;
      body.style.left = prevStyles.bodyLeft;
      body.style.right = prevStyles.bodyRight;
      body.style.width = prevStyles.bodyWidth;
      body.style.overscrollBehavior = prevStyles.bodyOverscrollBehavior;
      body.style.paddingRight = prevStyles.bodyPaddingRight;

      // Temporarily disable smooth scroll while restoring page position,
      // otherwise closing the modal may trigger a visible background glide.
      documentElement.style.scrollBehavior = 'auto';
      body.style.scrollBehavior = 'auto';
      window.scrollTo(0, scrollY);
      window.requestAnimationFrame(() => {
        documentElement.style.scrollBehavior = prevStyles.htmlScrollBehavior;
        body.style.scrollBehavior = prevStyles.bodyScrollBehavior;
      });
    };
  }, [open]);

  /**
   * 模态框打开时重置状态
   */
  useEffect(() => {
    if (open) {
      setPaymentStatus(null);
      setPaymentFailedOpen(false);
      setFailedMessage('');
      // 邮箱登录用户默认使用 PayPal
      if (user?.provider === 'email') {
        setPaymentMethod(0);
      }
    }
  }, [open, user?.provider]);

  // -----------------------------------------
  // PayPal 支付流程
  // -----------------------------------------

  /**
   * 创建 PayPal 订单
   * 调用后端 API 创建订单，获取 PayPal 订单 ID
   *
   * @param {Object} data - PayPal 回调数据
   * @param {Object} actions - PayPal actions 对象
   * @returns {Promise<string>} PayPal 订单 ID
   */
  const createOrder = async () => {
    setApiLoading(true, 'Creating PayPal order...');

    try {
      const paypalGetOrderRequest = {
        Productid: product.id,
        AppPlatform: 'platform_web',
        TypeCIDs: getTypeCIDsForProduct(),
      };

      const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.PAYPAL_CREATE_ORDER), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Fpid': user.id,
          "Gametoken": gameToken,
          "Playerid": user.playerid
        },
        body: JSON.stringify(paypalGetOrderRequest),
      });

      if (!response.ok) {
        tgaLogEvent('payment_page', { type: 'paypal_payment_error', api: API_ENDPOINTS.PAYPAL_CREATE_ORDER, msg: toString(response) });
        throw new Error(`Backend returned ${response.status}`);
      }

      const userData = await response.json();
      tgaLogEvent('payment_page', { type: 'paypal_payment', api: API_ENDPOINTS.PAYPAL_CREATE_ORDER, msg: JSON.stringify(userData) });
      order_id = userData.PaymentOrderID;

      const paypalOrderId =
        userData.OuterPaymentId ||
        userData.OuterPaymentID ||
        userData.PayPalOrderId ||
        userData.PayPalOrderID ||
        userData.PaypalOrderId ||
        userData.PaypalOrderID ||
        userData.OrderId ||
        userData.OrderID;

      if (!paypalOrderId) {
        throw new Error('PayPal order id missing in create order response');
      }

      return paypalOrderId;
    } finally {
      setApiLoading(false);
    }
  };

  /**
   * PayPal 支付成功回调
   * 验证订单并完成支付流程
   *
   * @param {Object} data - PayPal 回调数据
   * @param {Object} actions - PayPal actions 对象
   */
  const onApprove = async () => {
    setApiLoading(true, 'Verifying payment...');
    try {
      const result = await verifyOrder(order_id);
      tgaLogEvent('payment_page', { type: 'paypal_payment', api: API_ENDPOINTS.PAYPAL_VERIFY, msg: JSON.stringify(result) });

      if (result.PaymentOrderID == order_id) {
        setPaymentStatus({
          type: 'success',
          message: 'Payment completed successfully!'
        });

        const { setPaymentSuccessOpen, setLastOrder } = useStore.getState();
        const finalValue = getServerRewardValue(product);

        const order = {
          id: order_id,
          coins: finalValue,
          rewardTypeName: getProductTypeName(product.productType),
          productType: product.productType,
          amount: product.price,
          productName: product.name,
          timestamp: new Date().toISOString()
        };
        onClose();
        setLastOrder(order);
        setPaymentSuccessOpen(true);
      } else {
        setFailedMessage('Payment verification failed. Please try again.');
        setPaymentFailedOpen(true);
      }
    } catch (error) {
      setPaymentStatus({
        type: 'error',
        message: 'Payment verification failed. Please contact support.'
      });
      console.error('Error verifying order:', error);
    } finally {
      setApiLoading(false);
    }
  };

  /**
   * 验证 PayPal 订单
   * 调用后端 API 验证订单支付状态
   *
   * @param {string} orderId - 订单 ID
   * @returns {Promise<Object>} 验证结果
   */
  const verifyOrder = async (orderId) => {
    const paypalVerifyOrderRequest = {
      PaymentOrderID: orderId
    };

    const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.PAYPAL_VERIFY), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Fpid': user.id,
        "Gametoken": gameToken,
        "Playerid": user.playerid
      },
      body: JSON.stringify(paypalVerifyOrderRequest),
    });

    if (!response.ok) {
      throw new Error(`Backend returned ${response.status}`);
    }

    const userData = await response.json();
    return userData;
  };

  /**
   * PayPal 支付错误回调
   *
   * @param {Error} err - 错误对象
   */
  const onError = (err) => {
    const message = typeof err === 'string' ? err : err?.message || '';
    const isUserCancel =
      message.includes('Target window is closed') ||
      message.includes('Detected popup close') ||
      message.includes('popup close') ||
      message.includes('cancel');

    if (isUserCancel) {
      setPaymentStatus(null);
      return;
    }

    setPaymentStatus({
      type: 'error',
      message: message
        ? `PayPal error: ${message}`
        : 'An error occurred during payment. Please try again later.'
    });
  };

  // -----------------------------------------
  // Facebook 支付流程（当前已禁用）
  // -----------------------------------------

  /**
   * 处理 Facebook 支付
   * 完整的 Facebook 支付流程：创建订单 -> 调起支付 -> 验证结果
   */
  const handleFacebookPayment = async () => {
    setApiLoading(true, 'Processing Facebook payment...');
    try {
      setPaymentStatus({
        type: 'info',
        message: 'Processing payment...'
      });

      const orderData = await facebook_createOrder();
      tgaLogEvent('payment_page', { type: 'fb_payment', api: API_ENDPOINTS.FACEBOOK_CREATE_ORDER, msg: orderData });

      const paymentResult = await facebook_payOrder(orderData.PaymentOrderID, product.id);
      tgaLogEvent('payment_page', { type: 'fb_payment', api: 'facebook_payOrder', msg: JSON.stringify(paymentResult) });

      if (!paymentResult || paymentResult.status === 'cancelled') {
        setPaymentStatus(null);
        return;
      }

      if (paymentResult.status === 'failed' || paymentResult.error_code) {
        setFailedMessage('Facebook payment failed. Please try again.');
        setPaymentFailedOpen(true);
        setPaymentStatus(null);
        return;
      }

      const result = await facebook_verifyOrder(orderData.PaymentOrderID);
      tgaLogEvent('payment_page', { type: 'fb_payment', api: API_ENDPOINTS.FACEBOOK_VERIFY, msg: JSON.stringify(result) });

      if (result.status == 'ok') {
        setPaymentStatus({
          type: 'success',
          message: 'Payment completed successfully!'
        });

        const { setPaymentSuccessOpen, setLastOrder } = useStore.getState();
        const finalValue = getServerRewardValue(product);

        const order = {
          id: orderData.PaymentOrderID || order_id,
          coins: finalValue,
          rewardTypeName: getProductTypeName(product.productType),
          productType: product.productType,
          amount: product.price,
          productName: product.name,
          timestamp: new Date().toISOString()
        };
        onClose();
        setLastOrder(order);
        setPaymentSuccessOpen(true);
      } else if (result.status == 'skip') {
        setPaymentStatus(null);
      } else {
        setFailedMessage('Payment verification failed. Please try again.');
        setPaymentFailedOpen(true);
        setPaymentStatus(null);
      }
    } catch (error) {
      console.error('Facebook payment error:', error);
      setFailedMessage(error.message || 'An error occurred during payment. Please try again.');
      setPaymentFailedOpen(true);
      setPaymentStatus(null);
    } finally {
      setApiLoading(false);
    }
  };

  /**
   * 创建 Facebook 订单
   * @returns {Promise<Object>} 订单数据
   */
  const facebook_createOrder = async () => {
    const paypalGetOrderRequest = {
      Productid: product.id,
      AppPlatform: 'platform_web',
      TypeCIDs: getTypeCIDsForProduct(),
    };

    const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.FACEBOOK_CREATE_ORDER), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Fpid': user.id,
        "Gametoken": gameToken,
        "Playerid": user.playerid
      },
      body: JSON.stringify(paypalGetOrderRequest),
    });

    if (!response.ok) {
      throw new Error(`Backend returned ${response.status}`);
    }

    const userData = await response.json();
    order_id = userData.PaymentOrderID;
    return userData;
  };

  /**
   * 调起 Facebook 支付对话框
   *
   * @param {string} orderId - 订单 ID
   * @param {string} pId - 商品 ID
   * @returns {Promise<Object>} 支付结果
   */
  const facebook_payOrder = async (orderId, pId) => {
    let productUrl = getApiUrl(API_ENDPOINTS.FACEBOOK_PRODUCT_INFO) + '?payment_order_id=';
    let urlStr = productUrl + orderId + '&product_id=' + pId + '&uid=' + user.playerid;

    return new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error('Facebook SDK not loaded'));
        return;
      }

      FB.ui(
        {
          method: 'pay',
          action: 'purchaseitem',
          product: urlStr,
          quantity: 1,
          request_id: orderId + '',
        },
        function (data) {
          tgaLogEvent('payment_page', { type: 'fb_payment', api: 'fb_ui', msg: toString(data) });

          if (!data) {
            resolve({ status: 'cancelled' });
            return;
          }

          if (data.status === 'completed') {
            resolve({ status: 'completed', ...data });
          } else if (data.status === 'initiated') {
            resolve({ status: 'initiated', ...data });
          } else if (data.status === 'failed' || data.error_code) {
            resolve({ status: 'failed', error_code: data.error_code, ...data });
          } else {
            resolve({ status: 'unknown', ...data });
          }
        }
      );
    });
  };

  /**
   * 验证 Facebook 订单
   * @param {string} orderId - 订单 ID
   * @returns {Promise<Object>} 验证结果
   */
  const facebook_verifyOrder = async (orderId) => {
    const facebookVerifyOrderRequest = {
      PaymentOrderID: orderId
    };

    const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.FACEBOOK_VERIFY), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Fpid': user.id,
        "Gametoken": gameToken,
        "Playerid": user.playerid
      },
      body: JSON.stringify(facebookVerifyOrderRequest),
    });

    if (!response.ok) {
      throw new Error(`Backend returned ${response.status}`);
    }

    const userData = await response.json();
    return userData;
  };

  // -----------------------------------------
  // 渲染
  // -----------------------------------------

  // 未打开或无商品时不渲染
  if (!product || !open) return null;

  // PayPal 配置
  const paypalOptions = {
    "client-id": PAYPAL_CLIENT_ID,
    currency: product?.currency || "USD",
    intent: "capture",
    components: "buttons,funding-eligibility",
    locale: "en_US"
  };

  // 计算数值
  const baseValue = toSafeNumber(product.baseValue);
  const serverReward = getServerRewardValue(product);
  const isBaseProduct = product.productType === PRODUCT_TYPE.BASE_COIN ||
    product.productType === PRODUCT_TYPE.BASE_GEM;
  const isPromoProduct = !isBaseProduct;
  const isGemProduct = product.productType === PRODUCT_TYPE.NEW_STORE_GEM ||
    product.productType === PRODUCT_TYPE.BASE_GEM;
  const rewardAmountClass = isGemProduct
    ? 'text-[#198bc8] dark:text-[#16d4ff]'
    : 'text-[#a56e00] dark:text-[#ffd321]';
  const bonusPercent = isPromoProduct && baseValue > 0
    ? Math.floor(((serverReward - baseValue) / baseValue) * 100)
    : 0;
  const hasBonus = bonusPercent > 0;

  // 计算 PayPal 折扣
  const discountPercent = product.paypalPrice && product.paypalPrice < product.price
    ? Math.round(((product.price - product.paypalPrice) / product.price) * 100)
    : 0;
  const displayTotal = paymentMethod === 0 && product.paypalPrice ? product.paypalPrice : product.price;

  // 获取商品展示信息（与商品卡片一致）
  const productTypeName = getProductTypeName(product.productType);
  const productIcon = getProductIcon(product.productType, product.price);

  return (
    <>
      {/* 主支付模态框 */}
      <div className="fixed inset-0 z-[60] flex touch-none items-start justify-center overflow-y-auto overscroll-y-contain bg-[rgba(17,13,42,0.34)] px-4 py-3 backdrop-blur-[5px] sm:items-center sm:py-4 dark:bg-[rgba(4,2,12,0.58)]">
        <section className="max-h-[calc(100dvh-1.5rem)] w-full max-w-[62rem] touch-pan-y overflow-x-hidden overflow-y-auto overscroll-y-contain rounded-[2rem] border border-[#dfe7ff] bg-[linear-gradient(160deg,rgba(255,255,255,0.96),rgba(244,242,255,0.92))] text-[#1e1535] shadow-[0_34px_70px_rgba(87,108,180,0.22)] sm:max-h-[calc(100dvh-2rem)] dark:border-[#1e3a5f] dark:bg-[linear-gradient(160deg,#0f1a2e,#0a1420)] dark:text-white dark:shadow-[0_34px_70px_rgba(0,0,0,0.45)]">
          {/* 头部 */}
          <div className="flex items-center justify-between border-b border-[#dfe6ff] px-4 py-4 sm:px-7 sm:py-5 dark:border-[#1e3a5f]">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#6f7487] dark:text-[#8fa8c0]">
                Secure Payment
              </p>
              <h2 className="mt-2 font-display text-[1.45rem] font-black text-[#1e1535] sm:text-[2.1rem] dark:text-white">
                Complete Payment
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#dfe6ff] bg-white/72 font-display text-[1.45rem] font-black text-[#6f7487] transition hover:bg-white hover:text-[#1e1535] sm:h-11 sm:w-11 sm:text-[1.65rem] dark:border-[#1e3a5f] dark:bg-[#0f2640] dark:text-[#8fa8c0] dark:hover:border-[#2a4a6f] dark:hover:bg-[#153050] dark:hover:text-white"
            >
              ×
            </button>
          </div>

          {/* 支付状态提示 */}
          {paymentStatus && (
            <div className={`mx-4 mt-4 rounded-xl border px-4 py-3 text-sm sm:mx-7 ${
              paymentStatus.type === 'error' ? 'border-ruby/30 bg-ruby/10 text-ruby dark:border-[#5c1a1a] dark:bg-[#2a0a0a] dark:text-[#ff6b6b]' :
              paymentStatus.type === 'success' ? 'border-emerald/30 bg-emerald/10 text-emerald dark:border-[#1a4a2e] dark:bg-[#0a2a1a] dark:text-[#4ade80]' :
              'border-sapphire/30 bg-sapphire/10 text-sapphire dark:border-[#1a3a5f] dark:bg-[#0a1a2a] dark:text-[#74dfff]'
            }`}>
              {paymentStatus.message}
            </div>
          )}

          {/* 内容区域 */}
          <div className="grid min-w-0 gap-4 px-4 py-4 sm:gap-5 sm:px-7 sm:py-6 lg:grid-cols-[1.02fr_0.98fr]">
            {/* 订单摘要 */}
            <div className="min-w-0 rounded-[1.45rem] border border-[#e4d8ff] bg-[linear-gradient(180deg,#fffdf5_0%,#fff8e8_100%)] p-4 shadow-[0_18px_36px_rgba(0,0,0,0.08)] sm:rounded-[1.7rem] sm:p-5 dark:border-[#7b6522] dark:bg-[linear-gradient(180deg,rgba(60,45,10,0.85),rgba(40,30,5,0.9))] dark:shadow-[0_18px_36px_rgba(0,0,0,0.15)]">
              <p className="font-display text-[1.35rem] font-black text-[#1e1535] sm:text-[1.75rem] dark:text-white">
                Order Summary
              </p>

              <div className="mt-5 flex min-w-0 items-start gap-3 sm:mt-6 sm:items-center sm:gap-4">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[1rem] border border-[#e4d8ff] bg-[linear-gradient(180deg,#fffdf5_0%,#fff8e8_100%)] shadow-[0_10px_22px_rgba(0,0,0,0.08)] sm:h-24 sm:w-24 sm:rounded-[1.2rem] dark:border-[#7b6522] dark:bg-[linear-gradient(180deg,rgba(80,60,10,0.8),rgba(60,45,5,0.85))] dark:shadow-[0_10px_22px_rgba(0,0,0,0.15)]">
                  <img
                    src={productIcon}
                    alt={`${productTypeName} bundle`}
                    className="h-16 w-16 object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,0.18)] sm:h-[4.5rem] sm:w-[4.5rem]"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  {hasBonus ? (
                    <>
                      <p className="text-xs text-[#6f7487] line-through sm:text-sm dark:text-[#8fa8c0]">{baseValue.toLocaleString()}</p>
                      <p className={`font-display text-[1.45rem] font-black uppercase leading-[0.95] break-words sm:text-[2.55rem] ${rewardAmountClass}`}>
                        {serverReward.toLocaleString()} {productTypeName}
                      </p>
                      <span className="mt-1 inline-block rounded bg-ruby/20 px-2 py-0.5 text-[10px] font-bold text-ruby sm:text-xs dark:bg-[#5c1a1a] dark:text-[#ff6b6b]">
                        +{bonusPercent}% MORE
                      </span>
                    </>
                  ) : (
                    <p className={`font-display text-[1.45rem] font-black uppercase leading-[0.95] break-words sm:text-[2.55rem] ${rewardAmountClass}`}>
                      {serverReward.toLocaleString()} {productTypeName}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 space-y-3 border-t border-gold/20 pt-4 sm:mt-6 sm:space-y-3.5 sm:pt-5">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-[1.08rem] text-[#6f7487] sm:text-[1.45rem] dark:text-[#a0b0c0]">Subtotal</p>
                  <p className="text-[1.08rem] text-[#1e1535] sm:text-[1.45rem] dark:text-white">${product.price}</p>
                </div>
                {paymentMethod === 0 && product.paypalPrice && (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[1.08rem] text-sapphire sm:text-[1.45rem] dark:text-[#74dfff]">PayPal Discount</p>
                    <p className="text-[1.08rem] text-sapphire sm:text-[1.45rem] dark:text-[#74dfff]">-{discountPercent}%</p>
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-end justify-between gap-4 border-t border-gold/20 pt-4 sm:mt-6 sm:pt-5">
                <p className="text-[1.35rem] text-[#1e1535] sm:text-[1.9rem] dark:text-white">Total</p>
                <p className="text-right font-display text-[2.1rem] font-black leading-[0.9] text-gold sm:text-[2.95rem]">
                  ${displayTotal}
                </p>
              </div>
            </div>

            {/* 支付方式 */}
            <div className="min-w-0 rounded-[1.45rem] border border-[#dfe6ff] bg-white/60 p-4 shadow-[0_18px_36px_rgba(0,0,0,0.06)] sm:rounded-[1.7rem] sm:p-5 dark:border-[#1e3a5f] dark:bg-[#0a1929] dark:shadow-[0_18px_36px_rgba(0,0,0,0.3)]">
              <div className="flex items-center gap-3">
                <p className="font-display text-[1.2rem] font-black text-[#1e1535] sm:text-[1.45rem] dark:text-white">
                  Payment Method
                </p>
                {discountPercent > 0 && (
                  <span className="rounded-full bg-sapphire px-2.5 py-1 text-xs font-black text-white sm:px-3 sm:text-sm">
                    -{discountPercent}%
                  </span>
                )}
              </div>

              <div className="mt-4 h-[3px] w-full rounded-full bg-[#dfe6ff] dark:bg-[#1e3a5f]">
                <div className="h-full w-28 rounded-full bg-sapphire" />
              </div>

              {/* PayPal 优惠提示 */}
              <div className="mt-5 min-w-0 rounded-[1.2rem] border border-[#dfe6ff] bg-[linear-gradient(180deg,#f8fbff_0%,#f0f5ff_100%)] px-4 py-4 text-left shadow-[0_16px_32px_rgba(0,0,0,0.04)] sm:rounded-[1.5rem] sm:px-5 sm:py-5 dark:border-[#1e3a5f] dark:bg-[linear-gradient(180deg,#0a1929,#071220)] dark:shadow-[0_16px_32px_rgba(0,0,0,0.3)]">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="rounded-full bg-sapphire/15 px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-sapphire dark:bg-sapphire/25 dark:text-[#74dfff]">
                    Offer
                  </span>
                  <PayPalWordmark className="text-[1.05rem] leading-none sm:text-[1.25rem]" />
                </div>
                <p className="mt-3 text-[0.96rem] font-semibold leading-6 text-[#1e1535] sm:text-[1.1rem] dark:text-white">
                  Save {discountPercent}% with PayPal payment!
                </p>
              </div>

              {/* PayPal 按钮 */}
              <div className="mt-5 w-full min-w-0">
                <PayPalScriptProvider options={paypalOptions}>
                  <CustomPayPalButton
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={onError}
                    onCancel={() => {
                      setPaymentStatus(null);
                    }}
                  />
                </PayPalScriptProvider>
              </div>

              {/* Facebook 支付按钮（已禁用） */}
              {showFacebookPay && (
                <button
                  type="button"
                  onClick={handleFacebookPayment}
                  className="mt-4 w-full rounded-full border border-[#5f80ff]/30 bg-[linear-gradient(135deg,#2e67ff,#1d46bf)] px-5 py-3 font-display text-base font-black text-white transition hover:brightness-105"
                >
                  Pay with Facebook Pay
                </button>
              )}

              <p className="mt-4 text-center text-xs font-medium text-textSecondary/55 sm:text-sm dark:text-[#81a2bf]">
                Powered by <PayPalWordmark className="ml-1 text-[0.9rem] leading-none sm:text-[1rem]" />
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* 支付失败弹窗 */}
      {paymentFailedOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(17,13,42,0.34)] px-4 backdrop-blur-[5px] dark:bg-[rgba(4,2,12,0.58)]">
          <div className="w-full max-w-sm overflow-hidden rounded-[2rem] border border-[#dfe7ff] bg-[linear-gradient(160deg,rgba(255,255,255,0.96),rgba(244,242,255,0.92))] p-6 text-center shadow-[0_34px_70px_rgba(87,108,180,0.22)] dark:border-[#1e3a5f] dark:bg-[linear-gradient(160deg,#0f1a2e,#0a1420)] dark:shadow-[0_34px_70px_rgba(0,0,0,0.45)]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-ruby/15 text-4xl text-ruby">
              ✕
            </div>
            <h3 className="mt-4 font-display text-xl font-black text-[#1e1535] dark:text-white">
              Payment Failed
            </h3>
            <p className="mt-2 text-sm text-[#6f7487] dark:text-[#a0b0c0]">
              {failedMessage || 'Your payment could not be processed. Please try again.'}
            </p>
            <p className="mt-2 text-xs text-[#a8b1c8] dark:text-[#6a7a8a]">
              If this problem persists, please contact our customer support.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setPaymentFailedOpen(false)}
                className="flex-1 rounded-full bg-gold px-4 py-3 font-display text-sm font-black uppercase tracking-[0.12em] text-[#2b1300] transition hover:brightness-105"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={() => {
                  setPaymentFailedOpen(false);
                  onClose();
                }}
                className="flex-1 rounded-full border border-[#dfe6ff] px-4 py-3 font-display text-sm font-black uppercase tracking-[0.12em] text-[#6f7487] transition hover:bg-[#f2f5fb] dark:border-[#1e3a5f] dark:text-[#8fa8c0] dark:hover:bg-[#0f2640]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentModal;
